import { number } from "prop-types"

const padNumber = (number, length = 2) =>
  number.toString().padStart(length, '0')

const getTimeZoneOffset = (timeZoneOffsetInMinutes) => {
  const sign = timeZoneOffsetInMinutes > 0 ? '-' : '+'
  const hours = Math.floor(Math.abs(timeZoneOffsetInMinutes) / 60)
  const minutes = Math.abs(timeZoneOffsetInMinutes) % 60
  return sign + padNumber(hours) + '' + padNumber(minutes)
}

const getTimeZone = (date: Date) => {
  const timeZoneOffsetInMinutes = date.getTimezoneOffset()
  return getTimeZoneOffset(timeZoneOffsetInMinutes)
}

export const formatDateWithTimezone = (date) => {
  const day = padNumber(date.getDate(), 2)
  const month = padNumber(date.getMonth() + 1, 2) // Months are zero-based
  const year = date.getFullYear()
  const hours = padNumber(date.getHours(), 2)
  const minutes = padNumber(date.getMinutes(), 2)
  const seconds = padNumber(date.getSeconds(), 2)
  const milliseconds = padNumber(date.getMilliseconds(), 3)
  const timezoneOffset = -date.getTimezoneOffset()
  const sign = timezoneOffset >= 0 ? '+' : '-'
  const offsetHours = padNumber(Math.floor(Math.abs(timezoneOffset) / 60), 2)
  const offsetMinutes = padNumber(Math.abs(timezoneOffset) % 60, 2)

  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}.${milliseconds} ${sign}${offsetHours}${offsetMinutes}`
}
export const getFormattedDate = (date1: Date) => {
  if (!date1) {
    return ''
  }
  const timeZoneOffsetInMinutes = date1.getTimezoneOffset()
  const date = new Date(date1)
  const day = date.getDate()
  const month = date.getMonth() + 1
  const year = date.getFullYear()
  const hh = date.getHours()
  const mm = date.getMinutes()
  const currentDate = `${day}-${month}-${year} ${hh}:${mm} ${getTimeZoneOffset(
    timeZoneOffsetInMinutes
  )}`
  return currentDate
}

// Date-only formatter: dd-MM-yyyy (no time, no timezone)
export const formatDateDDMMYYYY = (date1: Date | null) => {
  if (!date1) {
    return ''
  }
  const date = new Date(date1)
  const day = padNumber(date.getDate(), 2)
  const month = padNumber(date.getMonth() + 1, 2)
  const year = date.getFullYear()
  return `${day}-${month}-${year}`
}

export const getEncodedFormattedDate = (date1: Date | null) => {
  if (!date1) {
    return ''
  }
  const timeZoneOffsetInMinutes = date1.getTimezoneOffset()
  let currentDate = ''
  const date = new Date(date1)
  const day = date.getDate()
  const month = date.getMonth() + 1
  const year = date.getFullYear()
  const hh = date.getHours()
  const mm = date.getMinutes()
  currentDate = `${day}-${month}-${year} ${hh}:${mm} ${getTimeZoneOffset(
    timeZoneOffsetInMinutes
  )}`
  return encodeURIComponent(currentDate)
}

export function getTimeOnly(dateString) {
  const date = new Date(dateString)
  const time = date.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    hour12: true, // Delete for 24-hour format
    minute: '2-digit',
  })
  return time
}

/** Parse API date strings (e.g. "19-02-2026 14:30 +0530") and ISO values. */
export function parseAppDateString(
  dateString: string | null | undefined
): Date | null {
  if (dateString == null) {
    return null
  }
  const trimmed = String(dateString).trim()
  if (!trimmed || trimmed === 'null' || trimmed === 'undefined') {
    return null
  }

  const appMatch = trimmed.match(
    /^(\d{1,2})-(\d{1,2})-(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?(?:\s+([+-]\d{4}))?$/
  )
  if (appMatch) {
    const [, dayStr, monthStr, yearStr, hourStr, minStr, secStr, tzStr] = appMatch
    const day = parseInt(dayStr, 10)
    const month = parseInt(monthStr, 10)
    const year = parseInt(yearStr, 10)
    const hour = hourStr ? parseInt(hourStr, 10) : 0
    const minute = minStr ? parseInt(minStr, 10) : 0
    const second = secStr ? parseInt(secStr, 10) : 0
    let iso = `${year}-${padNumber(month)}-${padNumber(day)}T${padNumber(hour)}:${padNumber(minute)}:${padNumber(second)}`
    if (tzStr) {
      iso += `${tzStr.slice(0, 3)}:${tzStr.slice(3)}`
    }
    const parsed = new Date(iso)
    if (!Number.isNaN(parsed.getTime())) {
      return parsed
    }
  }

  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }
  return parsed
}

export function getDisplayDateTime(dateString) {
  const date = parseAppDateString(dateString)
  if (!date) {
    return ''
  }
  return date.toLocaleString('en-IN', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    hour12: true, // Delete for 24-hour format
    minute: '2-digit',
  })
}

export function getDisplayDate(dateString) {
  const date = parseAppDateString(dateString)
  if (!date) {
    return ''
  }
  return date.toLocaleString('en-IN', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  })
}

export function getDisplayTime(dateString) {
  const date = parseAppDateString(dateString)
  if (!date) {
    return ''
  }
  return date.toLocaleString('en-IN', {
    hour: '2-digit',
    hour12: true, // Delete for 24-hour format
    minute: '2-digit',
  })
}

export const isValidGST = (gstin: string) => {
  return gstinRegex.test(gstin)
}

export const noEmptyRegex = /^(?!\s*$).+/
export const gstinRegex = /^([0-9]{2}[A-Z0-9]{13})|URP$/
export const pincodeRegex = /^[1-9][0-9]{5}$/
export const vehicleNumberRegex = /^[A-Z]{2}[0-9A-Z]{0,13}$/

export const isValidPincode = (pincode: string) => {
  return pincodeRegex.test(pincode)
}

export const isValidVehicleNumber = (vehicleNumber: string) => {
  const indianVehicleNumberRegex =
    /^[A-Z]{2}\s?[0-9]{1,2}\s?[A-Z]{1,3}\s?[0-9]{1,4}$/
  const x = indianVehicleNumberRegex.test(vehicleNumber)
  return indianVehicleNumberRegex.test(vehicleNumber)
}

export const isValidTransporterDocumentNumber = (documentNumber: string) => {
  // Regular expression to match the pattern of Transporter Document Number
  const transporterDocNumberRegex = /^[A-Z0-9]{10}$/

  // Check if the entered value matches the regex pattern
  return transporterDocNumberRegex.test(documentNumber)
}

export const getFormattedDateTimeIST = (date1: Date) => {
  let currentDate = ''
  const date = new Date(date1)
  const day = date.getDate()
  const month = date.getMonth() + 1
  const year = date.getFullYear()
  const hh = date.getHours()
  const mm = date.getMinutes()

  currentDate = `${day}-${month}-${year} ${hh}:${mm} ${getTimeZone(date1)}`
  return currentDate
}

export const replace12AmWithCurrentTime = (date1: Date) => {
  const date = new Date(date1)
  const day = date.getDate()
  const month = date.getMonth() + 1
  const year = date.getFullYear()
  const hh = date.getHours()
  const mm = date.getMinutes()
  if (hh === 0 && mm === 0) {
    const currentDate = new Date()
    const day2digit = day < 10 ? `0${day}` : `${day}`
    const month2digit = month < 10 ? `0${month}` : `${month}`
    const h2digit =
      currentDate.getHours() < 10
        ? `0${currentDate.getHours()}`
        : `${currentDate.getHours()}`
    const m2digit =
      currentDate.getMinutes() < 10
        ? `0${currentDate.getMinutes()}`
        : `${currentDate.getMinutes()}`
    const dateWithCurrentTime = `${month2digit}-${day2digit}-${year} ${h2digit}:${m2digit}:00`
    return new Date(dateWithCurrentTime)
  }
  return date1
}

export const getFormattedStartDateString = (date1: Date, encoded = true) => {
  let currentDate = ''
  const date = new Date(date1)
  const day = date.getDate()
  const month = date.getMonth() + 1
  const year = date.getFullYear()
  const timeZone = getTimeZone(date1).replace('+', '')
  if (encoded) {
    currentDate = `${day}-${month}-${year}%2000%3A00%20%2B${timeZone}`
  } else {
    currentDate = `${day}-${month}-${year} 00:00 +${timeZone}`
  }
  return currentDate
}

export const getFormattedEndDateString = (date1: Date, encoded = true) => {
  let currentDate = ''
  const date = new Date(date1)
  const day = date.getDate()
  const month = date.getMonth() + 1
  const year = date.getFullYear()
  const timeZone = getTimeZone(date1).replace('+', '')
  if (encoded) {
    currentDate = `${day}-${month}-${year}%2023%3A59%20%2B${timeZone}`
  } else {
    currentDate = `${day}-${month}-${year} 23:59 +${timeZone}`
  }
  return currentDate
}

export function getShortFormattedDate(dateString) {
  const date = new Date(dateString)
  const day = date.getDate()
  const month = date.toLocaleString('default', { month: 'short' })
  const year = date.getFullYear()

  return `${day < 10 ? `0${day}` : day} ${month} ${year}`
}

export function formatChallanDate(dateString: string): string {
  if (!dateString) {
    return ''
  }
  const date = new Date(dateString)
  const day = date.getDate()
  const month = date.toLocaleString('default', { month: 'short' })
  const hours = date.getHours()
  const minutes = date.getMinutes()
  const ampm = hours >= 12 ? 'pm' : 'am'
  const displayHours = hours % 12 || 12
  const displayMinutes = minutes < 10 ? `0${minutes}` : minutes

  return `${day} ${month} ${displayHours}:${displayMinutes}${ampm}`
}

export const getFormattedDateAndTime = (dateString) =>
  new Date(dateString).toLocaleString()

export const formatNumber = (number) => {
  if (!number) {
    return '0';
  }

  // Convert the number to an integer, then format it as a string with at least two digits
  const integerPart = parseInt(number).toString();

  // Pad the integer part to ensure it's at least two digits
  return integerPart.padStart(2, "0");
};

export const roundToTwo = (value: number): number =>
  Math.round((Number(value) + Number.EPSILON) * 100) / 100

const amountTwoDecimalFormatter = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export const formatAmountTwoDecimals = (
  value: number | string | null | undefined
): string => {
  const n = Number(value)
  if (!Number.isFinite(n)) {
    return amountTwoDecimalFormatter.format(0)
  }
  return amountTwoDecimalFormatter.format(roundToTwo(n))
}

export const currencyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 3,
})

const slipRateCurrencyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 6,
})

/** Slip OUT rate: INR with up to 6 fraction digits, truncated toward zero (not half-up). */
export const formatSlipRate = (value: number): string => {
  const n = Number(value)
  if (!Number.isFinite(n)) return slipRateCurrencyFormatter.format(0)
  const truncated = Math.trunc(n * 1e6) / 1e6
  return slipRateCurrencyFormatter.format(truncated)
}

export const indianMobileNumberRegex = /^(?:\+91|91)?[6-9]\d{9}$/

export const isValidMobileNumber = (value) => {
  return indianMobileNumberRegex.test(value)
}

/** Party/vendor autocomplete: "Name (mobile) - address". Omits (mobile) when null/empty; omits trailing " - address" when address empty. */
export function formatPartyAutocompleteLabel(
  name: string | null | undefined,
  mobile: string | null | undefined,
  address: string | null | undefined
): string {
  const namePart = (name ?? '').trim()
  const mobilePart = (mobile ?? '').trim()
  const addressPart = (address ?? '').trim()
  let s = namePart
  if (mobilePart) {
    s += s ? ` (${mobilePart})` : mobilePart
  }
  if (addressPart) {
    s += s ? ` - ${addressPart}` : addressPart
  }
  return s
}

/** Closed party Autocomplete value: "Name - State" or just "Name" when state empty. */
export function formatPartySelectedLabel(
  name: string | null | undefined,
  state: string | null | undefined
): string {
  const namePart = (name ?? '').trim()
  const statePart = (state ?? '').trim()
  if (!namePart) return statePart
  if (!statePart) return namePart
  return `${namePart} - ${statePart}`
}

/** Secondary line in party dropdown: "(mobile) - address" without the name. */
export function formatPartyOptionSecondaryLabel(
  mobile: string | null | undefined,
  address: string | null | undefined
): string {
  const mobilePart = (mobile ?? '').trim()
  const addressPart = (address ?? '').trim()
  let s = ''
  if (mobilePart) {
    s = `(${mobilePart})`
  }
  if (addressPart) {
    s += s ? ` - ${addressPart}` : addressPart
  }
  return s
}

export const getFirstDayoftheCurrentMonth = () => {
  const date = new Date(),
    year = date.getFullYear(),
    month = date.getMonth()
  return new Date(year, month, 1)
}

export const todayDDYYYY = () => {
  const date = new Date()
  const currentDay = String(date.getDate()).padStart(2, '0')
  const currentMonth = String(date.getMonth() + 1).padStart(2, '0')
  const currentYear = date.getFullYear()
  // we will display the date as DD-MM-YYYY
  const currentDate = `${currentDay}-${currentMonth}-${currentYear}`
  return currentDate
}

export const eWayformattedDate = (date) => {
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0') // Months are zero-based
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

export function loadScript(src) {
  return new Promise((resolve) => {
    const script = document.createElement('script')
    script.src = src
    script.onload = () => {
      resolve(true)
    }
    script.onerror = () => {
      resolve(false)
    }
    document.body.appendChild(script)
  })
}

const isValidEmail = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g

export const validateEmail = (e) => {
  return e && e.match(isValidEmail)
}

export const searchProperty = (obj, propName) => {
  for (const key in obj) {
    if (typeof obj[key] === 'object') {
      const result = searchProperty(obj[key], propName)
      if (result) {
        return result
      }
    } else if (key === propName) {
      return obj[key]
    }
  }
  return null // Property not found
}

export const addLeadingZero = (number) => {
  return number.length === 1 ? '0' + number : number
}

export function getEndDateOfMonth(date: Date) {
  // Get the year and month from the given date
  const year = date.getFullYear();
  const month = date.getMonth();

  // Create a new date for the last day of the current month
  const lastDayOfMonth = new Date(year, month + 1, 0); // The 0th day of the next month gives us the last day of the current month

  // Set the endDate to the last day of the month
  return lastDayOfMonth
}

export function ellipsizeString(str, maxLength) {
  if (!str || str.length <= maxLength) {
    return str; // No need to ellipsize
  }
  return str.substring(0, maxLength - 3) + '...'; // Trim and add ellipsis
}

export interface AddressComponents {
  addr1?: string | null;
  addr2?: string | null;
  place?: string | null;
  state?: string | null;
  pincode?: string | number | null;
}

/**
 * Formats a structured address object into a clean, comma-separated string.
 * Empty, null, or undefined fields are automatically omitted.
 */
export function formatAddress(address?: AddressComponents | null): string {
  if (!address) return '';

  const { addr1, addr2, place, state, pincode } = address;

  // Enforce structural order while cleaning up whitespace in a single pass
  const orderedParts = [
    addr1,
    addr2,
    place,
    state,
    pincode !== null && pincode !== undefined ? String(pincode) : ''
  ];

  return orderedParts
    .map(part => part?.trim())
    .filter((part): part is string => !!part) // Type guard to strip out empty strings/falsy values
    .join(', ');
}
